import React from 'react';
import { Phone, Mail, MapPin, Clock, Instagram, ExternalLink, Shield, Package, Star } from 'lucide-react';
import { laverieInfo } from '../data/mock';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';
import BasqueFlag from './BasqueFlag';

const Contact = () => {
  return (
    <section id="contact" className="section-padding bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9B1B30]/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Info */}
          <AnimateOnScroll animation="fadeLeft">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#9B1B30] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <MapPin size={16} />
                <span>Contact & accès</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
                Venez nous <span className="text-[#9B1B30]">rendre visite</span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-10 leading-relaxed">
                Notre laverie moderne est située au cœur de Bayonne, facilement accessible. 
                N'hésitez pas à nous contacter pour toute question.
              </p>

              {/* Contact items */}
              <div className="space-y-5">
                <a
                  href={laverieInfo.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-white/5 transition-colors"
                >
                  <div className="w-14 h-14 bg-[#9B1B30]/20 rounded-2xl flex items-center justify-center group-hover:bg-[#9B1B30] transition-colors flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#9B1B30] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">Adresse</p>
                    <p className="text-gray-400">{laverieInfo.address}</p>
                    <p className="text-gray-400">{laverieInfo.city}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 rounded-2xl">
                  <div className="w-14 h-14 bg-[#006633]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#006633]" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">Horaires</p>
                    <p className="text-gray-400">Ouvert {laverieInfo.hours.days}</p>
                    <p className="text-gray-400">{laverieInfo.hours.open} - {laverieInfo.hours.close}</p>
                  </div>
                </div>

                <a
                  href={`tel:${laverieInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-white/5 transition-colors"
                >
                  <div className="w-14 h-14 bg-[#9B1B30]/20 rounded-2xl flex items-center justify-center group-hover:bg-[#9B1B30] transition-colors flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#9B1B30] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">Téléphone</p>
                    <p className="text-gray-400">{laverieInfo.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${laverieInfo.email}`}
                  className="flex items-start gap-4 group p-4 rounded-2xl hover:bg-white/5 transition-colors"
                >
                  <div className="w-14 h-14 bg-[#006633]/20 rounded-2xl flex items-center justify-center group-hover:bg-[#006633] transition-colors flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#006633] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">Email</p>
                    <p className="text-gray-400">{laverieInfo.email}</p>
                  </div>
                </a>
              </div>

              {/* Features */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-2xl p-4 flex items-center gap-3 border border-white/10">
                  <Shield className="w-6 h-6 text-[#006633]" />
                  <div>
                    <p className="font-semibold text-white text-sm">Vidéosurveillance</p>
                    <p className="text-gray-500 text-xs">24h/24</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-2xl p-4 flex items-center gap-3 border border-white/10">
                  <Package className="w-6 h-6 text-[#9B1B30]" />
                  <div>
                    <p className="font-semibold text-white text-sm">Point Pickup</p>
                    <p className="text-gray-500 text-xs">Colis 7j/7</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Map */}
          <AnimateOnScroll animation="fadeRight">
            <div className="relative h-full min-h-[500px]">
              <div className="bg-gray-800 rounded-3xl overflow-hidden h-full shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2899.7!2d-1.4747!3d43.4929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd51408f6f6f6f6f%3A0x0!2s5%20Rue%20Tour%20de%20Sault%2C%2064100%20Bayonne!5e0!3m2!1sfr!2sfr!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '500px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation Lavoir des Remparts"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              
              {/* Overlay card */}
              <a
                href={laverieInfo.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-6 left-6 right-6 bg-white rounded-2xl p-5 shadow-2xl flex items-center justify-between hover:shadow-3xl transition-shadow group"
              >
                <div className="flex items-center gap-4">
                  <BasqueFlag size={45} />
                  <div>
                    <p className="font-bold text-gray-900">Lavoir des Remparts</p>
                    <p className="text-sm text-gray-500">{laverieInfo.address}</p>
                  </div>
                </div>
                <div className="bg-[#9B1B30] text-white p-3 rounded-xl group-hover:bg-[#B91C3B] transition-colors">
                  <ExternalLink size={20} />
                </div>
              </a>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

export default Contact;
